import React, { useState } from "react";
import { Typography, Stack, Button, Box, Alert } from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";

import AppModal from "../common/AppModal";
import AppButton from "../buttons/AppButton";
import { useAppDispatch, useAppSelector } from "../../redux/store";
import { uploadBankStatement } from "../../redux/actions/bankTransactionStagingActions";

interface UploadBankStatementModalProps {
  open: boolean;
  onClose: () => void;
}

const UploadBankStatementModal: React.FC<UploadBankStatementModalProps> = ({
  open,
  onClose,
}) => {
  const dispatch = useAppDispatch();
  const { loadingStagedTransactionUpload, errorUploadingStagedTransaction } =
    useAppSelector((state) => state.bankTransactionStaging);

  const [file, setFile] = useState<File | null>(null);

  const handleUpload = (): void => {
    if (file) {
      dispatch(uploadBankStatement(file));
    }
  };

  const handleClose = (): void => {
    setFile(null);
    onClose();
  };

  return (
    <AppModal
      open={open}
      onClose={handleClose}
      title="Upload Bank Statement"
      width="sm"
      loading={loadingStagedTransactionUpload}
    >
      <Stack spacing={3}>
        {errorUploadingStagedTransaction && (
          <Alert severity="error">{errorUploadingStagedTransaction}</Alert>
        )}
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          Upload your bank statement in Excel (.xlsx) format. Ensure the columns
          are: <b>Date, Reference, Narrative, Amount</b> (in that exact order).
        </Typography>

        <Box
          sx={{
            border: "2px dashed",
            borderColor: "divider",
            borderRadius: 2,
            p: 4,
            textAlign: "center",
            bgcolor: "background.paper",
          }}
        >
          <Button
            variant="outlined"
            component="label"
            startIcon={<UploadFileIcon />}
          >
            Choose Excel File
            <input
              type="file"
              hidden
              accept=".xlsx, .xls"
              onChange={(e) => {
                if (e.target.files && e.target.files.length > 0) {
                  setFile(e.target.files[0]);
                }
              }}
            />
          </Button>
          {file && (
            <Typography
              variant="subtitle2"
              sx={{ mt: 2, color: "primary.main", fontWeight: 600 }}
            >
              Selected: {file.name}
            </Typography>
          )}
        </Box>

        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
          <Button
            onClick={handleClose}
            color="inherit"
            disabled={loadingStagedTransactionUpload}
          >
            Cancel
          </Button>
          <AppButton
            variant="contained"
            color="primary"
            onClick={handleUpload}
            disabled={!file || loadingStagedTransactionUpload}
          >
            Process & Match
          </AppButton>
        </Box>
      </Stack>
    </AppModal>
  );
};

export default UploadBankStatementModal;
